import { type PracticePreferenceVerifyRegistrationSections } from 'primary-schema';

import { type Common } from './common.js';
import { type OffsetPaginationResponse } from './models/common.js';
import {
  type CreateValues,
  type Encounter,
  type EncounterView,
  type EncounterWithPatient,
  type EncounterWithPatientAndPayment,
  type FindAllOptions,
  type FindAllViewOptions,
  type IntakeForm,
  type Timeframe,
  type UpdateValues,
  type ValidationKind,
} from './models/encounter.js';
import { type FamilyMedicalHistory } from './models/familyMedicalHistory.js';
import { type Fax } from './models/fax.js';
import { type ScheduledAppointment } from './models/index.js';
import { type AggregatedValidations } from './models/note.js';
import { type GuarantorToExport } from './models/patient/guarantor.js';
import {
  type InsuranceInformationUpdates,
  type TerminatedInsurance,
} from './models/patient/insuranceInformation.js';
import { type PrevisitFormSet } from './models/previsit/formSet.js';
import { type OutputKind } from './models/scenario.js';
import { isTruthy } from './utils.js';

export class API {
  common: Common;
  baseEndpoint = '/encounters';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options?: FindAllOptions): Promise<EncounterWithPatient[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    const { encounters } = await this.common.getJSON(url);
    return encounters;
  }

  async findAllView(
    options?: FindAllViewOptions
  ): Promise<OffsetPaginationResponse<EncounterView>> {
    const url = this.common.constructPath('/encounters/view', options);
    return await this.common.getJSON(url);
  }

  async findOneById(encounterId: string): Promise<EncounterWithPatientAndPayment> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}`);
    const { encounter } = await this.common.getJSON(url);
    return encounter;
  }

  async findAppointmentNote(encounterId: string): Promise<string> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/appointment-note`);
    const { appointmentNote } = await this.common.getJSON(url);
    return appointmentNote;
  }

  async findClinicalComment(encounterId: string): Promise<string> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/clinical-comment`);
    const { clinicalComment } = await this.common.getJSON(url);
    return clinicalComment;
  }

  async findGeneralComment(encounterId: string): Promise<string> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/general-comment`);
    const { generalComment } = await this.common.getJSON(url);
    return generalComment;
  }

  async findJobPayloadGeneralComment(encounterId: string): Promise<string | null> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${encounterId}/general-comment/job`
    );
    const { generalComment } = await this.common.getJSON(url);
    // Normalize a missing comment to null so callers get a defined value.
    return isTruthy(generalComment) ? generalComment : null;
  }

  async findGuarantor(encounterId: string): Promise<GuarantorToExport> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/guarantors`);
    return await this.common.getJSON(url);
  }

  async findFamilyMedicalHistory(encounterId: string): Promise<FamilyMedicalHistory> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${encounterId}/family-medical-history`
    );
    return await this.common.getJSON(url);
  }

  async findScheduledAppointment(
    encounterId: string
  ): Promise<ScheduledAppointment.ScheduledAppointment> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${encounterId}/scheduled-appointment`
    );
    const { scheduledAppointment } = await this.common.getJSON(url);
    return scheduledAppointment;
  }

  async findTerminatedInsurances(encounterId: string): Promise<TerminatedInsurance[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${encounterId}/insurance-information/terminated`
    );
    const { insurances } = await this.common.getJSON(url);
    return insurances;
  }

  async findAllFaxes(id: string): Promise<Fax[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/faxes`);
    return await this.common.getJSON(url);
  }

  async upsertFormSet(
    args: {
      idsToAdd: string[];
      idsToRemove: string[];
    } & ({ ehrInformationId: string } | { encounterId: string })
  ): Promise<PrevisitFormSet> {
    const url = this.common.constructPath(`/forms`);
    return await this.common.patchJSON(url, args);
  }

  async findAllIntakeForms(encounterId: string): Promise<IntakeForm[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/forms`);
    return await this.common.getJSON(url);
  }

  async findVerifyRegistrationSections(
    encounterId: string
  ): Promise<{ verifyRegistrationSections: PracticePreferenceVerifyRegistrationSections[] }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${encounterId}/verify-registration`
    );
    return await this.common.getJSON(url);
  }

  async getPatientInsuranceInformationUpdates(
    encounterId: string
  ): Promise<InsuranceInformationUpdates> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${encounterId}/insurance-information`
    );
    const { patientInsuranceInformationUpdates } = await this.common.getJSON(url);
    return patientInsuranceInformationUpdates;
  }

  async create(encounter: CreateValues): Promise<Encounter> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, encounter);
  }

  async runFingerprint(
    encounterId: string,
    body: { kinds: OutputKind[]; queryOnly?: boolean }
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/fingerprint`);
    return await this.common.postJSON(url, body);
  }

  async update(encounterId: string, values: UpdateValues): Promise<Encounter> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}`);
    return await this.common.putJSON(url, values);
  }

  async getValidation(
    encounterId: string,
    applicableValidations: ValidationKind[] = []
  ): Promise<{ validation: AggregatedValidations }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/validation`, {
      applicableValidations: applicableValidations.join(','),
    });
    return await this.common.getJSON(url);
  }

  async remove(encounterId: string): Promise<boolean> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}`);
    const { success } = await this.common.deleteJSON(url);
    return success;
  }

  async unremove(encounterId: string): Promise<Encounter> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/unremove`);
    const { encounter } = await this.common.postJSON(url, {});
    return encounter;
  }

  async bulkUnremove(encounterIds: string[]): Promise<{ encounters: Encounter[] }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/bulk/unremove`);
    return await this.common.postJSON(url, { encounterIds });
  }

  async copy(encounterId: string): Promise<boolean> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/copy`);
    const { success } = await this.common.putJSON(url);
    return success;
  }

  async findAllForEhrSystemByIds(args: {
    encounterId: string;
    activeOnly: boolean;
    isFuture: boolean;
    timeframe: Timeframe;
  }): Promise<EncounterWithPatient[]> {
    const { encounterId, ...query } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/relative`, query);
    const { encounters } = await this.common.getJSON(url);
    return encounters;
  }

  async inPersonCheckIn(encounterId: string): Promise<void> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${encounterId}/in-person-check-in`);
    return await this.common.postJSON(url, {});
  }
}
